import { useParams, Link } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, MapPin,
  Users, Calendar, Mail,
  FileText, BarChart3, Zap,
  AlertTriangle, Building2, TrendingUp, Clock
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getIndustryBySlug, INDUSTRIES, type Industry } from '../../data/industries';
import {
  getLocationByStateAndSlug,
  getCitiesByState,
  STATES,
  type LocationData,
} from '../../data/locations';

// Deterministic hash for consistent variation per combo
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Generate localized pain points that vary by industry + location
function getLocalizedPainPoints(industry: Industry, location: LocationData) {
  const city = location.city;
  const stateAbbr = location.stateAbbr;
  const hash = simpleHash(industry.slug + location.slug);

  const openers = [
    `${city}'s competitive ${industry.name.toLowerCase()} market`,
    `In ${city}, ${stateAbbr}`,
    `Across the ${city} area`,
    `For ${industry.name.toLowerCase()} businesses in ${city}`,
    `${city}-area employers in ${industry.name.toLowerCase()}`,
    `The ${city} ${industry.name.toLowerCase()} scene`,
  ];

  return industry.painPoints.map((point, i) => {
    const opener = openers[(hash + i) % openers.length];
    return {
      title: point.title,
      description: `${opener} — ${point.description.charAt(0).toLowerCase()}${point.description.slice(1)}`,
    };
  });
}

// Generate stats that combine industry and location data
function getComboStats(industry: Industry, location: LocationData) {
  const city = location.city;
  const hash = simpleHash(industry.slug + location.slug);
  const baseStats = industry.stats;

  const localLabels = [
    `in ${city}`,
    `for ${city} businesses`,
    `across ${location.stateAbbr}`,
    `in the ${city} area`,
  ];

  return baseStats.map((stat, i) => ({
    number: stat.number,
    label: `${stat.label} ${localLabels[(hash + i) % localLabels.length]}`,
  }));
}

// Pick 4 features most relevant to the industry
function getIndustryFeatures(industry: Industry, location: LocationData) {
  const city = location.city;
  const allFeatures = [
    {
      icon: Zap,
      title: 'Instant Auto-Replies',
      desc: `Every ${industry.name.toLowerCase()} applicant in ${city} gets an automatic response within seconds of applying, keeping candidates engaged.`,
      industries: ['restaurants-hospitality', 'retail-sales', 'cleaning-janitorial', 'salon-beauty'],
    },
    {
      icon: Calendar,
      title: 'Self-Service Scheduling',
      desc: `${city} candidates self-schedule interviews through your Google Calendar. No phone tag with busy ${industry.name.toLowerCase()} managers.`,
      industries: ['healthcare-medical', 'construction-trades', 'legal-law-firms', 'real-estate'],
    },
    {
      icon: FileText,
      title: 'Custom Application Forms',
      desc: `Build ${industry.name.toLowerCase()}-specific forms that capture certifications, experience, and availability from ${city} applicants.`,
      industries: ['healthcare-medical', 'construction-trades', 'automotive', 'fitness-wellness'],
    },
    {
      icon: BarChart3,
      title: 'Applicant Tracking Dashboard',
      desc: `Track every ${industry.name.toLowerCase()} candidate in ${city} from application to hire in one organized dashboard.`,
      industries: ['technology-it', 'insurance-finance', 'education', 'logistics-warehousing'],
    },
    {
      icon: Mail,
      title: 'Email Campaigns',
      desc: `Send targeted emails to ${industry.name.toLowerCase()} candidates across the ${city} area to keep your talent pipeline active.`,
      industries: ['retail-sales', 'restaurants-hospitality', 'pet-services', 'salon-beauty'],
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      desc: `Your entire ${city} hiring team can review ${industry.name.toLowerCase()} applicants and leave notes together in real time.`,
      industries: ['legal-law-firms', 'education', 'healthcare-medical', 'technology-it'],
    },
    {
      icon: TrendingUp,
      title: 'Submission Analytics',
      desc: `Track form views, submission rates, and which ${city} job boards drive the most ${industry.name.toLowerCase()} applicants.`,
      industries: ['technology-it', 'real-estate', 'insurance-finance', 'logistics-warehousing'],
    },
    {
      icon: Building2,
      title: 'Multi-Location Support',
      desc: `Managing multiple ${industry.name.toLowerCase()} locations in ${city}? Create separate forms per site and track applicants independently.`,
      industries: ['restaurants-hospitality', 'retail-sales', 'cleaning-janitorial', 'fitness-wellness'],
    },
  ];

  // Score each feature by relevance to this industry
  const scored = allFeatures.map((f) => ({
    ...f,
    score: f.industries.includes(industry.slug) ? 2 : 1,
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 4);
}

// Get the state slug from stateAbbr
function getStateSlug(stateAbbr: string): string {
  const state = STATES.find((s) => s.abbr === stateAbbr);
  return state ? state.slug : stateAbbr.toLowerCase();
}

// Generate industry-specific subtitle that varies by location
function getSubtitle(industry: Industry, location: LocationData): string {
  const city = location.city;
  const hash = simpleHash(industry.slug + location.slug);
  const subtitles = [
    `${industry.description.split('.')[0]} in ${city}, ${location.stateAbbr}. iHireFast helps local ${industry.name.toLowerCase()} businesses automate applications, interview scheduling, and candidate communication.`,
    `Finding qualified ${industry.name.toLowerCase()} staff in ${city} is competitive. iHireFast gives ${city} employers the tools to attract, screen, and hire top candidates faster than the competition.`,
    `${city}'s ${industry.name.toLowerCase()} employers need to move fast. iHireFast automates your hiring pipeline so you can fill open ${industry.name.toLowerCase()} positions in days, not weeks.`,
  ];
  return subtitles[hash % subtitles.length];
}

export default function IndustryLocationPage() {
  const { industrySlug, state: stateParam, locationSlug } = useParams<{
    industrySlug: string;
    state: string;
    locationSlug: string;
  }>();

  const industry = industrySlug ? getIndustryBySlug(industrySlug) : undefined;
  const location =
    stateParam && locationSlug ? getLocationByStateAndSlug(stateParam, locationSlug) : undefined;

  if (!industry || !location) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h1>
            <p className="text-gray-600 mb-8">
              We could not find this industry-location combination. Browse our options below.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/industries" className="btn-primary">
                View All Industries
              </Link>
              <Link to="/locations" className="btn-secondary">
                View All Locations
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const cityName = location.city;
  const fullName = `${cityName}, ${location.stateAbbr}`;
  const stateSlug = stateParam || getStateSlug(location.stateAbbr);
  const painPoints = getLocalizedPainPoints(industry, location);
  const stats = getComboStats(industry, location);
  const features = getIndustryFeatures(industry, location);
  const subtitle = getSubtitle(industry, location);

  // Other industries in the same location
  const otherIndustries = INDUSTRIES.filter((i) => i.slug !== industry.slug).slice(0, 6);

  // Same industry in nearby locations (other cities in same state)
  const nearbyCities = getCitiesByState(location.stateAbbr)
    .filter((l) => l.slug !== location.slug)
    .slice(0, 8);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
              <Link
                to="/industries"
                className="hover:text-gray-900 transition-colors"
              >
                Industries
              </Link>
              <span>/</span>
              <Link
                to={`/industries/${industry.slug}`}
                className="hover:text-gray-900 transition-colors"
              >
                {industry.name}
              </Link>
              <span>/</span>
              <Link
                to={`/locations/${stateSlug}`}
                className="hover:text-gray-900 transition-colors"
              >
                {location.state}
              </Link>
              <span>/</span>
              <span className="text-gray-700 font-medium">{cityName}</span>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-8">
              <MapPin className="w-4 h-4" />
              {industry.icon} {industry.name} in {fullName}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              Hire{' '}
              <span className="gradient-text">{industry.name}</span>
              {' '}Staff in {cityName}, {location.stateAbbr}
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
              >
                Start Hiring in {cityName.split(' ')[0]}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
              >
                Talk to Sales
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.number}</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Localized Pain Points */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Local Challenges
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {industry.name} hiring challenges in {cityName}
            </h2>
            <p className="text-lg text-gray-600">
              These are the top hiring pain points for {industry.name.toLowerCase()} businesses in{' '}
              {fullName} — and how iHireFast solves each one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {painPoints.map((point, i) => (
              <div key={i} className="card p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              iHireFast tools for {industry.name.toLowerCase()} hiring in {cityName}
            </h2>
            <p className="text-lg text-gray-600">
              Purpose-built features that help {cityName} {industry.name.toLowerCase()} businesses
              hire faster and with less effort.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature, i) => (
              <div key={i} className="card p-6 group hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-600 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Start hiring {industry.name.toLowerCase()} staff in {cityName} in 3 steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: '01',
                title: `Build your ${industry.name.toLowerCase()} form`,
                desc: `Create a custom application form with fields tailored to ${industry.name.toLowerCase()} positions in ${cityName}. Drag-and-drop — no code needed.`,
                icon: FileText,
              },
              {
                step: '02',
                title: 'Automate outreach',
                desc: `Every ${cityName} applicant gets an instant auto-reply and can self-schedule interviews through your Google Calendar.`,
                icon: Clock,
              },
              {
                step: '03',
                title: 'Track and hire',
                desc: `Review ${industry.name.toLowerCase()} applicants in your dashboard, move them through your pipeline, and fill positions faster in ${fullName}.`,
                icon: BarChart3,
              },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-primary-600" />
                </div>
                <div className="text-xs font-bold text-primary-600 mb-2">STEP {step.step}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Start Hiring {industry.name} Staff in {cityName} Today
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Join {industry.name.toLowerCase()} businesses in {fullName} using iHireFast to
              streamline recruiting. Free to start, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links: Other industries in same location */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Other industries hiring in {cityName}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Explore more hiring solutions for {fullName}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {otherIndustries.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}/${stateSlug}/${location.slug}`}
                className="card p-3 group hover:shadow-md transition-all duration-300 text-center"
              >
                <div className="text-xl mb-1">{ind.icon}</div>
                <h4 className="text-xs font-semibold text-gray-900 group-hover:text-primary-600 transition-colors leading-tight">
                  {ind.name}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links: Same industry in nearby locations */}
      {nearbyCities.length > 0 && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="container-custom">
            <div className="text-center mb-8">
              <h3 className="text-lg font-semibold text-gray-900">
                {industry.name} hiring in other {location.state} cities
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Find {industry.name.toLowerCase()} staff across {location.state}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {nearbyCities.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/industries/${industry.slug}/${stateSlug}/${loc.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 transition-all"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {loc.city}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back links */}
      <section className="py-8 bg-white border-t border-gray-200">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link
              to={`/industries/${industry.slug}`}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {industry.name} Overview
            </Link>
            <Link
              to={`/locations/${stateSlug}/${location.slug}`}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Hiring in {cityName}
            </Link>
            <Link
              to="/industries"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              All Industries
            </Link>
            <Link
              to={`/locations/${stateSlug}`}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {location.state} Locations
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
